import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import DateStringInput from '@repo/ui/date-string-input'
import Input from '@repo/ui/input'
import MultiSelectInput from '@repo/ui/multiselect-input'
import RadioInput from '@repo/ui/radio-input'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'

import { getTripAddresses } from '../../../api/trip-addresses/get-trip-addresses'
import { getTrips } from '../../../api/trips/get-trips'
import {
  tripStepTypeMapping,
  TripTypeEnum,
  tripTypeMapping
} from '../../../lib/types/enums/trip/trip-type.enum'
import { TripStepTypeEnum } from '../../../lib/types/enums/trip-bookings/trip-step-type.enum'
import { TripProperties } from '../../../lib/types/models/trip/properties.trip'
import { type TripFolderFilters } from '../../../lib/types/models/trip-folder/trip-folder-filtes'

interface Props {
  filter?: TripFolderFilters
  onFilterChange: (filter: TripFolderFilters) => void
}

export function TripFolderFilter({ filter, onFilterChange }: Props) {
  const [filterValidationError, setFilterValidationError] = useState<string>()

  function setFilter<K extends keyof TripFolderFilters>(key: K, value: TripFolderFilters[K]) {
    onFilterChange({ ...filter, [key]: value })
  }

  const { data: trips } = useQuery({
    queryKey: ['trips'],
    queryFn: () => getTrips()
  })

  const { data: tripAddresses } = useQuery({
    queryKey: ['trip-addresses'],
    queryFn: () => getTripAddresses()
  })

  const handleYearBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const year = e.target.value
    if (!year || isNaN(parseInt(year))) {
      setFilter('year', undefined)
      return
    }

    const parsedYear = parseInt(year)

    if (parsedYear < 1990 || parsedYear > 2100) {
      setFilterValidationError("L'année doit être entre 1990 et 2100")
      setFilter('year', undefined)
      return
    }

    setFilter('year', parsedYear)
    setFilterValidationError(undefined)
  }

  useEffect(() => {
    if (filter?.date) {
      setFilter('year', undefined)
    } else if (filter?.year) {
      setFilter('date', undefined)
    }
  }, [filter?.date, filter?.year])

  return (
    <div className={'flex flex-col gap-3'}>
      <div>
        <Typography variant="label-medium" color={tailwindColors.red.DEFAULT}>
          {filterValidationError}
        </Typography>
      </div>
      <div className="flex items-center gap-2">
        <DateStringInput
          disabled={filter?.year !== undefined}
          label={'Date'}
          value={filter?.date}
          onChange={(date) => setFilter('date', date)}
        />
        <Input
          className="w-32"
          type="number"
          label="Année"
          min={1990}
          max={2100}
          disabled={filter?.date !== undefined}
          value={filter?.year}
          onChange={(e) => {
            const value = e.target.value
            if (value && !isNaN(parseInt(value))) {
              setFilter('year', parseInt(value))
            }
          }}
          onBlur={handleYearBlur}
          placeholder="YYYY"
        />
      </div>

      <MultiSelectInput
        label={'Type de trajet'}
        variant={'primary'}
        options={Object.values(TripStepTypeEnum).map((type) => ({
          label: tripStepTypeMapping[type].label,
          value: type
        }))}
        selectedOptions={filter?.directionalities?.map((d) => ({
          label: tripStepTypeMapping[d].label,
          value: d
        }))}
        onChange={(directionalities) => setFilter('directionalities', directionalities)}
      />

      <MultiSelectInput
        label={'Type de voyage'}
        variant={'primary'}
        options={Object.values(TripTypeEnum).map((tripType) => ({
          label: tripTypeMapping[tripType].label,
          value: tripType
        }))}
        selectedOptions={filter?.tripTypes?.map((tripType) => ({
          label: tripTypeMapping[tripType].label,
          value: tripType
        }))}
        onChange={(tripTypes) => setFilter('tripTypes', tripTypes)}
      />

      <MultiSelectInput
        searchable
        label={'Voyages'}
        variant={'primary'}
        options={
          trips?.data.map((trip) => ({
            label: TripProperties.getTripLabel(trip),
            value: trip.id
          })) ?? []
        }
        selectedOptions={filter?.tripIds?.map((tripId) => {
          const trip = trips?.data.find((trip) => trip.id === tripId)
          return {
            label: TripProperties.getTripLabel(trip),
            value: tripId
          }
        })}
        onChange={(tripIds) => setFilter('tripIds', tripIds)}
      />

      <MultiSelectInput
        label={'Lieu de départ'}
        variant={'primary'}
        searchable
        options={tripAddresses?.data.map((tripAddress) => ({
          label: tripAddress.label,
          value: tripAddress.id
        }))}
        onChange={(selectedOptions) => setFilter('departurePlaceIds', selectedOptions)}
        selectedOptions={filter?.departurePlaceIds?.map((id) => ({
          label: tripAddresses?.data.find((tripAddress) => tripAddress.id === id)?.label ?? '',
          value: id
        }))}
      />

      <MultiSelectInput
        label={"Lieux d'arrivée"}
        variant={'primary'}
        searchable
        options={tripAddresses?.data.map((tripAddress) => ({
          label: tripAddress.label,
          value: tripAddress.id
        }))}
        onChange={(selectedOptions) => setFilter('arrivalPlaceIds', selectedOptions)}
        selectedOptions={filter?.arrivalPlaceIds?.map((id) => ({
          label: tripAddresses?.data.find((tripAddress) => tripAddress.id === id)?.label ?? '',
          value: id
        }))}
      />

      <div className="flex gap-4">
        <RadioInput
          checked={filter?.locked === true}
          label={'Dossier verrouillé'}
          onCheck={() => onFilterChange({ ...filter, locked: filter?.locked ? undefined : true })}
        />
        <RadioInput
          checked={filter?.locked === false}
          label={'Dossier non verrouillé'}
          onCheck={() => {
            onFilterChange({ ...filter, locked: filter?.locked === false ? undefined : false })
          }}
        />
      </div>
    </div>
  )
}
